import autoBind from "auto-bind";
import { isEmpty } from "lodash";
import qs from "qs";
import React from "react";
import { createPortal } from "react-dom";
import { Redirect } from "react-router";
import { compose } from "redux";

import { UrlProvider } from "../../../../../api/UrlProvider";
import { getRegisterWithRedirectUrl } from "../../../../../lib/auth/authRedirectUrl";
import config from "../../../../../config";
import connect from "../../../../../i18n/connect";
import withPaymentDetails from "../../../../../hocs/withPaymentDetails";
import withPaymentForm from "../../../../../hocs/withPaymentForm";
import withUserData from "../../../../../hocs/withUserData";
import Loader from "../../../common/Loader";
import Link from "../../../common/SquaberLink";
import LandingTypography from "../../../landingPage/components/LandingTypography";
import FormatCurrency from "../../../landingPage/pages/Homepage/components/FormatCurrency";
import SquaberAppIcon from "../../../landingPage/pages/Homepage/assets/app_icon_squaber.png";
import PaymentModal from "../../../landingPage/pages/Plans/components/PaymentModal";
import {
  CARD_MODEL,
  PAYMENT_METHODS,
  SUPPORTED_PAYMENT_METHODS
} from "../../PaymentForm/modules/default";
import extractOfferPaymentMethods from "../../PaymentForm/utils/extractPaymentMethodsFromOffer";

const OFFER_UNAVAILABLE_REDIRECT_MS = 5000;

const isValidOfferId = offerId =>
  typeof offerId === "string" && /^\d+$/.test(offerId);

function OfferUnavailableView({ translate, locale }) {
  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      window.location.assign(config.webAppUrl);
    }, OFFER_UNAVAILABLE_REDIRECT_MS);

    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <LandingTypography className="offer-stripe-checkout landing-typography_dark">
      <div className="offer-stripe-checkout__chrome p-6">
        <div className="flex justify-center pt-8 pb-6">
          <Link to={translate("lp_squaber-logo_href")}>
            <img
              src={SquaberAppIcon}
              alt={translate("lp_squaber-logo_alt")}
              height={48}
              width={48}
            />
          </Link>
        </div>
        <div className="flex flex-col align-center text-center gap-4 mx-auto py-8 max-w-lg">
          <p className="text-sm text-black m-0">
            {translate(
              "This offer link is invalid or the offer is no longer available."
            )}
          </p>
          <p className="text-sm text-black opacity-70 m-0">
            {translate("Redirecting you to Squaber...")}
          </p>
          <Link
            to={UrlProvider.getUrl("fe.homeWithLocale", { locale })}
            className="button-1 button-1_clear mt-2"
          >
            {translate("Return to the homepage")}
          </Link>
        </div>
      </div>
    </LandingTypography>
  );
}

class OfferStripeCheckoutCore extends React.Component {
  constructor(props) {
    super(props);

    autoBind.react(this);
    this.autoFinalizeSent = false;
    this.consentModalTriggered = false;
    this.state = { showConsentModal: false, consentDismissed: false };
  }

  componentDidMount() {
    this.props.fetchPaymentDetails();
  }

  getUserIsLoggedIn() {
    const {
      userData,
      location: { search }
    } = this.props;
    const searchParams = qs.parse(search, { ignoreQueryPrefix: true });

    return !!userData || !!searchParams.auth_token_for_login;
  }

  isPaymentDetailsReady() {
    return this.props.loading === false;
  }

  hasActiveSubscription() {
    const { paymentDetails } = this.props;

    return !!paymentDetails?.has_subscription;
  }

  renderOfferCheckoutChrome(children) {
    const { translate } = this.props;

    return (
      <LandingTypography className="offer-stripe-checkout landing-typography_dark">
        <div className="offer-stripe-checkout__chrome p-6">
          <div className="flex justify-center pt-8 pb-6">
            <Link to={translate("lp_squaber-logo_href")}>
              <img
                src={SquaberAppIcon}
                alt={translate("lp_squaber-logo_alt")}
                height={48}
                width={48}
              />
            </Link>
          </div>
          {children}
        </div>
      </LandingTypography>
    );
  }

  renderOfferLoadingContent(translate, phase) {
    const isFetchPhase = phase === "fetch";
    const title = isFetchPhase
      ? translate("Loading your offer")
      : translate("Preparing your checkout");
    const body = isFetchPhase
      ? translate(
          "We're gathering your plan details. This only takes a moment — you can relax while we finish up."
        )
      : translate(
          "We're connecting you to our secure payment page. This usually takes just a few seconds. Thank you for your patience."
        );

    return (
      <div className="flex flex-col align-center text-center gap-4 mx-auto py-8 max-w-lg">
        <h2 className="text-xl.1 font-semibold text-black m-0 normal-case">
          {title}
        </h2>
        <p className="text-sm text-black opacity-70 m-0 max-w-md">{body}</p>
        <div className="mt-2">
          <Loader />
        </div>
      </div>
    );
  }

  renderOfferLoadingPanel(translate, phase) {
    return this.renderOfferCheckoutChrome(
      this.renderOfferLoadingContent(translate, phase)
    );
  }

  renderOfferSummary(selectedPlanName, price) {
    return (
      <div className="bg-gray rounded p-4 flex flex-col gap-6 max-w-lg mx-auto w-full mt-6">
        {selectedPlanName}
        {price}
      </div>
    );
  }

  renderConsentDismissedIdle(translate, locale) {
    return (
      <div className="flex flex-col align-center text-center gap-4 mx-auto py-8 max-w-lg">
        <p className="text-sm text-black opacity-70 m-0">
          {translate("Accept the terms to continue to payment.")}
        </p>
        <button
          type="button"
          className="button-1"
          onClick={() => {
            this.setState({ showConsentModal: true, consentDismissed: false });
          }}
        >
          {translate("Review terms and continue")}
        </button>
        <Link
          to={UrlProvider.getUrl("fe.homeWithLocale", { locale })}
          className="button-1 button-1_clear"
        >
          {translate("Return to the homepage")}
        </Link>
      </div>
    );
  }

  getSelectedPlan() {
    const { selectedOffer, offers } = this.props;

    if (!selectedOffer || isEmpty(selectedOffer)) {
      return null;
    }

    return offers?.data?.offers?.[0]?.plans?.find(
      plan => plan.name === selectedOffer.plan_name
    );
  }

  isDataReady() {
    const {
      selectedOffer,
      selectedOfferLoading,
      selectedOfferError,
      offers
    } = this.props;

    if (selectedOfferLoading === true) {
      return false;
    }

    if (offers?.loading === true) {
      return false;
    }

    if (selectedOfferError) {
      return true;
    }

    return !isEmpty(selectedOffer);
  }

  resolveStripeSubscriptionMethod() {
    return SUPPORTED_PAYMENT_METHODS.find(
      m => m.name === PAYMENT_METHODS.STRIPE_SUBSCRIPTION
    );
  }

  isStripeSubscriptionEligible() {
    const { selectedOffer } = this.props;

    if (!selectedOffer || isEmpty(selectedOffer)) {
      return false;
    }

    const { paymentMethods, subscriptionId } = extractOfferPaymentMethods(
      selectedOffer
    );

    return (
      subscriptionId != null &&
      paymentMethods.indexOf(PAYMENT_METHODS.STRIPE_SUBSCRIPTION) !== -1
    );
  }

  finalizeStripeSubscription(agreementContent) {
    const {
      finalizePayment,
      match: {
        params: { offerId }
      },
      selectedOffer
    } = this.props;
    const { subscriptionId } = extractOfferPaymentMethods(selectedOffer);
    const method = this.resolveStripeSubscriptionMethod();

    finalizePayment({
      paymentMethod: method.name,
      paymentMethodAlias: method.alias,
      cardModel: CARD_MODEL,
      offer: offerId,
      subscriptionId,
      agreementContent: agreementContent ?? undefined,
      fallbackPaymentFormOfferId: offerId
    });
  }

  componentDidUpdate(prevProps) {
    const prevOfferId = prevProps.match?.params?.offerId;
    const nextOfferId = this.props.match?.params?.offerId;

    if (prevOfferId !== nextOfferId) {
      this.autoFinalizeSent = false;
      this.consentModalTriggered = false;
      this.setState({ showConsentModal: false, consentDismissed: false });
    }

    const {
      loading,
      userData,
      selectedOffer,
      selectedOfferError,
      paymentFormLoading
    } = this.props;

    if (!this.getUserIsLoggedIn()) {
      return;
    }

    if (!this.isDataReady() || selectedOfferError) {
      return;
    }

    if (isEmpty(selectedOffer)) {
      return;
    }

    if (loading === true || !this.isPaymentDetailsReady()) {
      return;
    }

    if (this.hasActiveSubscription()) {
      return;
    }

    if (!this.isStripeSubscriptionEligible()) {
      return;
    }

    if (paymentFormLoading) {
      return;
    }

    const selectedPlan = this.getSelectedPlan();
    const hasAgreement = !!selectedPlan?.agreement_content;

    if (hasAgreement) {
      if (!this.consentModalTriggered) {
        this.consentModalTriggered = true;
        this.setState({ showConsentModal: true });
      }

      return;
    }

    if (!this.autoFinalizeSent) {
      this.autoFinalizeSent = true;
      this.finalizeStripeSubscription(undefined);
    }
  }

  render() {
    const {
      translate,
      locale,
      match: {
        params: { offerId }
      },
      userData,
      loading,
      selectedOffer,
      selectedOfferLoading,
      selectedOfferError,
      offers,
      paymentDetails,
      paymentFormLoading
    } = this.props;

    const { showConsentModal, consentDismissed } = this.state;

    if (!this.getUserIsLoggedIn()) {
      return (
        <Redirect to={getRegisterWithRedirectUrl({ locale })} />
      );
    }

    const offerFetchPending =
      (selectedOfferLoading === true || selectedOfferLoading === null) &&
      !selectedOfferError;

    if (offerFetchPending || loading === true || offers?.loading === true) {
      return this.renderOfferLoadingPanel(translate, "fetch");
    }

    if (selectedOfferError || isEmpty(selectedOffer)) {
      return <OfferUnavailableView translate={translate} locale={locale} />;
    }

    if (this.isPaymentDetailsReady() && this.hasActiveSubscription()) {
      const planName =
        paymentDetails?.plan_name || userData?.current_plan?.name || "";

      return this.renderOfferCheckoutChrome(
        <div className="flex flex-col align-center text-center gap-4 mx-auto py-8 max-w-lg">
          <p className="text-sm text-black m-0">
            {translate(
              "You already have an active subscription (%{planName}).",
              { planName }
            )}
          </p>
          <Link
            to={UrlProvider.getUrl("fe.homeWithLocale", { locale })}
            className="button-1 button-1_clear mt-2"
          >
            {translate("Return to the homepage")}
          </Link>
        </div>
      );
    }

    if (!this.isStripeSubscriptionEligible()) {
      return (
        <Redirect
          to={UrlProvider.getUrl("fe.paymentForm", {
            locale,
            offerId
          })}
        />
      );
    }

    const selectedPlan = this.getSelectedPlan();
    const hasAgreement = !!selectedPlan?.agreement_content;
    const waitingConsentOpen =
      hasAgreement &&
      !showConsentModal &&
      !consentDismissed &&
      !this.consentModalTriggered;
    const selectedPlanName = selectedPlan ? (
      <div className="flex gap-2 align-center mt-4">
        <span className="text-xs text-black opacity-65">
          {translate("plans_payment-bar_current-plan")}
        </span>
        <strong className="text-base text-black font-bold">
          {selectedPlan.name}
        </strong>
      </div>
    ) : (
      <strong className="text-base text-black font-bold">
        {selectedOffer.plan_name || ""}
      </strong>
    );

    const price = (
      <div className="flex align-center">
        <span className="text-base text-black opacity-70 font-bold">
          <FormatCurrency
            locale={locale}
            value={Number(selectedOffer.offer_amount)}
          />
        </span>
      </div>
    );

    const showBusy =
      paymentFormLoading ||
      (!hasAgreement && !this.autoFinalizeSent && this.isDataReady()) ||
      waitingConsentOpen;

    const showConsentBackdrop =
      hasAgreement && (showConsentModal || consentDismissed);

    return this.renderOfferCheckoutChrome(
      <>
        {showBusy
          ? this.renderOfferLoadingContent(translate, "checkout")
          : null}
        {showConsentBackdrop && !showBusy
          ? this.renderOfferSummary(selectedPlanName, price)
          : null}
        {hasAgreement && consentDismissed && !showConsentModal && !showBusy
          ? this.renderConsentDismissedIdle(translate, locale)
          : null}
        {hasAgreement && showConsentModal
          ? createPortal(
              <PaymentModal
                selectedPlanName={selectedPlanName}
                price={price}
                description={null}
                selectedPlan={selectedPlan}
                userData={userData}
                onPaymentButtonClick={async () => {
                  this.finalizeStripeSubscription(
                    selectedPlan?.agreement_content
                  );
                  this.setState({ showConsentModal: false });
                }}
                hideModal={() => {
                  this.setState({
                    showConsentModal: false,
                    consentDismissed: true
                  });
                }}
              />,
              document.body
            )
          : null}
      </>
    );
  }
}

const OfferStripeCheckoutConnected = compose(
  withPaymentForm,
  withPaymentDetails,
  withUserData
)(OfferStripeCheckoutCore);

function OfferStripeCheckoutShell(props) {
  const offerId = props.match?.params?.offerId;

  if (!isValidOfferId(offerId)) {
    return (
      <OfferUnavailableView translate={props.translate} locale={props.locale} />
    );
  }

  return <OfferStripeCheckoutConnected {...props} />;
}

export default connect()(OfferStripeCheckoutShell);
